import { useTranslation } from "react-i18next";
import { useState } from "react";
import { createColumnHelper } from "@tanstack/react-table";
import StyledTable from "@/components/styled/StyledTable";
import { useDeleteUserBo, useGetUsersBo } from "@/api/user.api";
import { GetAllCompanyDto } from "@/types/api/api-types";
import { ConfirmationModal } from "@/components/shared/confirmation.modal";
import { Trash2, Pencil } from "lucide-react";

type UserRow = GetAllCompanyDto["user"][number];

const columnHelper = createColumnHelper<UserRow>();

type GeneralInfoUserTableProps = {
  handleEdit: (user: UserRow) => void;
};

export const GeneralInfoUserTable = ({ handleEdit }: GeneralInfoUserTableProps) => {
  const { t } = useTranslation("boParameters");
  const [userToDelete, setUserToDelete] = useState<string | null>(null);
  const { data: users } = useGetUsersBo({ isDeleted: false });
  const { mutateAsync: deleteUser } = useDeleteUserBo();

  const superAdminCount = users?.filter((user) => user.role === "SUPERADMIN").length;

  const columns = [
    columnHelper.display({
      id: "user",
      header: t("usersTable.userHeader"),
      meta: "text-left",
      enableSorting: false,
      cell: (info) => (
        <div className="flex flex-col items-start gap-2">
          <p className="text-base font-medium text-black capitalize">
            {info.row.original.firstName + " " + info.row.original.lastName}
          </p>
          <p className="text-sm text-gray-600">{info.row.original.email}</p>
        </div>
      ),
    }),
    columnHelper.display({
      id: "role",
      header: t("usersTable.roleHeader"),
      meta: "text-left",
      cell: (info) => (
        <div className="w-fit rounded-lg border border-gray-200 bg-gray-500 px-3 py-1">
          <p className="text-sm font-medium text-white">{info.row.original.role}</p>
        </div>
      ),
    }),
    columnHelper.display({
      id: "details",
      header: "",
      cell: (data) => (
        <div className="flex w-full justify-center gap-1">
          {superAdminCount && superAdminCount > 1 && (
            <button
              onClick={() => setUserToDelete(data.row.original.id)}
              className="rounded-full p-2 hover:bg-gray-200"
            >
              <Trash2 className="text-gray-600" />
            </button>
          )}
          <div className="flex w-full flex-row justify-end">
            <button
              className="w-fit rounded-full p-2 hover:bg-gray-200"
              onClick={() => handleEdit(data.row.original)}
            >
              <Pencil className="text-gray-600" />
            </button>
          </div>
        </div>
      ),
    }),
  ];

  return (
    <div className="flex flex-col gap-10">
      <StyledTable data={users || []} columns={columns} />
      <ConfirmationModal
        open={!!userToDelete}
        onClose={() => setUserToDelete(null)}
        title={t("usersTable.deleteTitle")}
        text={t("usersTable.deleteText")}
        confirm={async () => {
          if (userToDelete) await deleteUser(userToDelete);
        }}
      />
    </div>
  );
};
